// 2026-09-22 (user request): Halaman DETAIL PENGELUARAN MINGGUAN — rincian
// pengeluaran satu periode (Senin–Minggu) untuk satu grup project. Dibuka dari
// tombol "Detail Pengeluaran Mingguan" di halaman detail project. Edit/Hapus
// baris expense dipindahkan ke sini (dulu di dropdown inline tab Pengeluaran).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Pencil, Trash2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { mondayOf, weekLabelOf, buildUnifiedExpenses, rowsForHead } from "@/lib/expenseWeeks";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import CatatPengeluaranDialog from "@/components/finance-report/CatatPengeluaranDialog";
import PengeluaranItemDetailDialog from "@/components/finance-report/PengeluaranItemDetailDialog";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function PengeluaranMingguPage() {
  const { pid, week } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canPerm, ready: accessReady } = useAccess();
  const canView = canPerm(user, "finance_report", "view");
  const canUpdate = canPerm(user, "finance_report", "update");
  const canDelete = canPerm(user, "finance_report", "delete");
  const isOps = pid === "ops-kantor";

  const [projects, setProjects] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  // 2026-06-21 (user request): alur review penanda "kemungkinan duplikat".
  const [dupRow, setDupRow] = useState(null);
  // 2026-10-19 (user request): klik nominal → dialog rincian item pembentuknya.
  const [detailRow, setDetailRow] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [projRes, pcRes] = await Promise.all([
        api.get("/active-projects"),
        api.get("/petty-cash").catch(() => ({ data: [] })),
      ]);
      setProjects(Array.isArray(projRes.data) ? projRes.data : []);
      setPettyCash(Array.isArray(pcRes.data) ? pcRes.data : (pcRes.data?.items || []));
    } catch (e) {
      setLoadError("Server tidak merespons atau koneksi terputus.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (canView) load(); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const head = useMemo(() => projects.find((p) => p.id === pid) || null, [projects, pid]);

  const rows = useMemo(() => {
    const all = rowsForHead(buildUnifiedExpenses(projects, pettyCash), pid);
    return all
      .filter((r) => (mondayOf(r.date) || "tanpa-tanggal") === week)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [projects, pettyCash, pid, week]);

  const expenseRows = useMemo(() => {
    const out = [];
    for (const p of projects) for (const e of p.expense_entries || []) out.push({ ...e, __project: p, __source: "expense" });
    return out;
  }, [projects]);

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const title = isOps ? "Operasional Kantor (Petty Cash)" : (head?.project_name || head?.wo_number || "Detail Pengeluaran");

  const handleDelete = async () => {
    if (!delRow) return;
    try {
      await api.delete(`/active-projects/${delRow.__project_id || delRow.project_id}/expense/${delRow.id}`);
      toast.success("Pengeluaran dihapus");
      setDelRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const handleDismissDup = async () => {
    if (!dupRow) return;
    try {
      await api.post(`/active-projects/${dupRow.__project_id || dupRow.project_id}/expense/${dupRow.id}/dismiss-duplicate`);
      toast.success("Entri ditandai sudah direview — penanda duplikat dihapus");
      setDupRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menandai review");
    }
  };

  if (!accessReady) {
    return <div className="p-6 text-sm text-slate-400 italic">Memuat izin akses…</div>;
  }
  if (!canView) {
    return (
      <div className="p-6" data-testid="pengeluaran-minggu-no-access">
        <div className="max-w-md mx-auto mt-16 border border-rose-200 bg-rose-50 p-6 text-center">
          <AlertTriangle size={28} className="text-rose-600 mx-auto mb-2" />
          <h2 className="font-display text-lg font-bold text-rose-800 mb-1">Akses Ditolak</h2>
          <p className="text-xs text-rose-700 leading-relaxed">Anda belum punya izin melihat <b>Laporan Keuangan</b>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="pengeluaran-minggu-page">
      {/* Header + kembali */}
      <div className="flex items-start gap-3">
        <button type="button" onClick={() => navigate(`/finance-report/pengeluaran/${pid}`)}
          data-testid="pengeluaran-minggu-back"
          className="mt-1 inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100">
          <ArrowLeft size={12} /> Kembali
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Keuangan · Pengeluaran · Detail Mingguan</div>
          <h1 className="text-2xl font-bold text-[#0B1D3A] tracking-tight mt-0.5 font-display" data-testid="pengeluaran-minggu-title">{weekLabelOf(week)}</h1>
          <p className="text-xs text-slate-500 mt-1 tabular-nums" data-testid="pengeluaran-minggu-subtitle">
            {title} · {rows.length} pengeluaran · Total <b className="text-rose-700">{formatIDR(total)}</b>
          </p>
        </div>
      </div>

      {loadError && (
        <div className="border border-rose-200 bg-rose-50 p-6 text-center" data-testid="pengeluaran-minggu-error">
          <AlertTriangle size={24} className="mx-auto text-rose-400 mb-2" />
          <div className="text-sm font-bold text-rose-700">Gagal memuat data</div>
          <div className="text-xs text-rose-600 mt-1 mb-3">{loadError} Data Anda aman — ini masalah sementara.</div>
          <button type="button" onClick={load} data-testid="pengeluaran-minggu-retry"
            className="px-4 py-2 text-xs font-semibold bg-[#0B1D3A] text-white hover:bg-[#12294f]">Coba Lagi</button>
        </div>
      )}

      <CatatPengeluaranDialog
        open={!!editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        projects={projects}
        expenseRows={expenseRows}
        onChanged={load}
        editRow={editRow}
      />
      <PengeluaranItemDetailDialog
        open={!!detailRow}
        onOpenChange={(v) => { if (!v) setDetailRow(null); }}
        row={detailRow}
      />
      <ConfirmDialog
        open={!!delRow}
        onOpenChange={(v) => { if (!v) setDelRow(null); }}
        title="Hapus Pengeluaran?"
        description={delRow ? `${fmtDate(delRow.date)} · ${formatIDR(delRow.amount)}${delRow.note ? ` · ${delRow.note}` : ""}. Tindakan ini tidak bisa dibatalkan.` : ""}
        confirmLabel="Hapus"
        onConfirm={handleDelete}
        variant="danger"
        testid="pengeluaran-minggu-confirm-delete"
      />
      <ConfirmDialog
        open={!!dupRow}
        onOpenChange={(v) => { if (!v) setDupRow(null); }}
        title="Tandai Sudah Direview?"
        description={dupRow ? `${fmtDate(dupRow.date)} · ${formatIDR(dupRow.amount)}${dupRow.note ? ` · ${dupRow.note}` : ""}. Konfirmasi bahwa entri ini BUKAN duplikat — penanda "kemungkinan duplikat" akan dihapus dan aksi ini tercatat di audit.` : ""}
        confirmLabel="Ya, Sudah Direview"
        onConfirm={handleDismissDup}
        testid="pengeluaran-minggu-confirm-dismiss-dup"
      />

      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-xs italic text-slate-400" data-testid="pengeluaran-minggu-loading">Memuat pengeluaran…</div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-300">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-700">Tanggal</th>
                <th className="px-3 py-2 overline text-slate-700">Sumber</th>
                <th className="px-3 py-2 overline text-slate-700">Project / Detail</th>
                <th className="px-3 py-2 overline text-slate-700">Catatan</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Jumlah</th>
                <th className="px-3 py-2 overline text-slate-700">Oleh</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic" data-testid="pengeluaran-minggu-empty">Tidak ada pengeluaran pada periode ini.</td></tr>
              )}
              {rows.map((r) => {
                const dupIds = Array.isArray(r.raw?.possible_duplicate_of) ? r.raw.possible_duplicate_of : [];
                return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pengeluaran-minggu-row-${r.raw.id}`}>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{r.date ? fmtDate(r.date) : "-"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border ${r.source === "expense" ? "bg-slate-100 border-slate-300 text-slate-700" : "bg-amber-100 border-amber-300 text-amber-800"}`}>
                      {r.source === "expense" ? "Expense" : "Petty"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-800">
                    {r.project ? (
                      <>
                        <div className="font-semibold">{r.project.project_name || r.project.wo_number}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {r.project.client && <span className="text-[10px] text-slate-500">{r.project.client}</span>}
                          {r.subLabel && (
                            <span className={`inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded-sm ${
                              r.subKind === "main" ? "bg-sky-50 border-sky-300 text-sky-700"
                                : r.subKind === "vo" ? "bg-purple-50 border-purple-300 text-purple-700"
                                : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>{r.subLabel}</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="italic text-slate-500">Operasional Kantor</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[280px]" title={r.note}>
                    <div className="truncate">{r.note || "-"}</div>
                    {dupIds.length > 0 && (
                      <span
                        data-testid={`pengeluaran-minggu-dup-${r.raw.id}`}
                        title="Sistem menandai entri ini mirip entri lain (tanggal + nominal + referensi/catatan sama) — perlu review manual."
                        className="inline-flex items-center gap-1 mt-0.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border bg-amber-50 border-amber-300 text-amber-800">
                        Kemungkinan Duplikat
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">
                    {/* 2026-10-19 (user request): nominal clickable → rincian item */}
                    <button type="button" onClick={() => setDetailRow(r)}
                      data-testid={`pengeluaran-minggu-amount-${r.raw.id}`}
                      title="Klik untuk melihat rincian item pembentuk nominal ini"
                      className="font-bold text-rose-700 underline decoration-dotted decoration-rose-300 underline-offset-2 hover:text-rose-900 hover:decoration-rose-600 transition-colors">
                      {formatIDR(r.amount)}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.by}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {/* 2026-09-22: aksi mengikuti recordKind (record asli). */}
                    {(r.recordKind || r.source) === "expense" ? (
                      <div className="inline-flex gap-1">
                        {canUpdate && dupIds.length > 0 && (
                          <button type="button" onClick={() => setDupRow(r.raw)} title="Tandai sudah direview — hapus penanda duplikat"
                            data-testid={`pengeluaran-minggu-dupdismiss-${r.raw.id}`}
                            className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-50"><CheckCheck size={13} /></button>
                        )}
                        {canUpdate && (
                          <button type="button" onClick={() => setEditRow(r.raw)} title="Edit pengeluaran"
                            data-testid={`pengeluaran-minggu-edit-${r.raw.id}`}
                            className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><Pencil size={13} /></button>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => setDelRow(r.raw)} title="Hapus pengeluaran"
                            data-testid={`pengeluaran-minggu-del-${r.raw.id}`}
                            className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50"><Trash2 size={13} /></button>
                        )}
                        {!canUpdate && !canDelete && <span className="text-[10px] text-slate-400 italic">—</span>}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic" title="Petty cash dikelola dari halaman Weekly Petty Cash">via Petty Cash</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
